import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Mountain, Radio } from "lucide-react";
import { HERO, IMAGES } from "../data/content";
import { scrollToTarget, TopoLines } from "./ui-bits";

const ease = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.05, delay, ease }}>
        {children}
      </motion.span>
    </span>
  );
}

function OrbitBadge() {
  return (
    <div className="absolute -left-8 top-6 z-10 hidden h-32 w-32 md:block lg:-left-14" data-testid="hero-orbit-badge">
      <div className="relative h-full w-full">
        <svg viewBox="0 0 120 120" className="spin-slow h-full w-full" aria-hidden="true">
          <defs>
            <path id="orbitCircle" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" />
          </defs>
          <text className="fill-cream/80" style={{ fontSize: "9px", letterSpacing: "2.6px", fontFamily: "'JetBrains Mono', monospace" }}>
            <textPath href="#orbitCircle">EXPEDITION PORTFOLIO • HIMALAYA • SINCE 2017 •&nbsp;</textPath>
          </text>
        </svg>
        <span className="absolute inset-0 m-auto flex h-12 w-12 items-center justify-center rounded-full bg-ember text-night">
          <Mountain className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 90]);

  // cursor parallax — avatar tilts and drifts toward the pointer
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 55, damping: 14 });
  const sy = useSpring(py, { stiffness: 55, damping: 14 });
  const rotY = useTransform(sx, [-1, 1], [-5, 5]);
  const rotX = useTransform(sy, [-1, 1], [4, -4]);
  const tx = useTransform(sx, [-1, 1], [-12, 12]);
  const ty = useTransform(sy, [-1, 1], [-9, 9]);
  const bx = useTransform(sx, [-1, 1], [10, -10]);
  const by = useTransform(sy, [-1, 1], [7, -7]);

  const track = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    px.set(((e.clientX - r.left) / r.width) * 2 - 1);
    py.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
      onPointerMove={track}
      onPointerLeave={() => {
        px.set(0);
        py.set(0);
      }}
    >      {/* base sky */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_18%,#1B2B21_0%,#0B120E_62%)]" />
      <div className="stars opacity-40" aria-hidden="true" />
      <TopoLines className="pointer-events-none absolute -left-40 bottom-0 h-[560px] w-[760px] opacity-50" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 pb-28 pt-40 md:px-8 lg:grid-cols-[1.12fr_1fr]">
        {/* left — type */}
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="flex flex-wrap items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-ember md:text-[11px]"
          >
            <span className="h-px w-12 bg-ember" />
            {HERO.eyebrow}
          </motion.p>

          <h1 className="mt-6 font-display text-6xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-7xl" data-testid="hero-heading">
            <MaskedLine delay={0.35}>Graphic</MaskedLine>
            <MaskedLine delay={0.48}><span className="text-outline">Designer</span></MaskedLine>
            <MaskedLine delay={0.61}><span className="text-ember">&amp; Web</span> Designer</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8, ease }}
            className="mt-6 max-w-lg text-base leading-relaxed text-cream/75 md:text-lg"
          >
            {HERO.sub.split("—")[0]}— <span className="text-white">{HERO.sub.split("—")[1]}</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToTarget("#about")}
              data-testid="hero-cta-explore"
              className="btn-primary group flex items-center gap-3 px-7 py-4 font-mono text-[11px] font-bold tracking-[0.22em]"
            >
              EXPLORE THE ROUTE <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
            </button>
            <button
              onClick={() => scrollToTarget("#contact")}
              data-testid="hero-cta-dispatch"
              className="btn-fill flex items-center gap-3 px-7 py-4 font-mono text-[11px] font-bold tracking-[0.22em]"
            >
              SEND A DISPATCH <Radio className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8, ease }}
            className="mt-10 flex flex-wrap gap-3"
            data-testid="hero-stat-chips"
          >
            {[["50+", "WEBSITES DESIGNED"], ["◆", "DESIGN · WEB · CONTENT"], ["DESIGNING SINCE", "2017"]].map(([v, l]) => (
              <span key={l} className="border border-cream/15 px-4 py-2.5 font-mono text-[10px] tracking-[0.18em] text-cream/60">
                <span className="mr-2 font-bold text-ember">{v}</span>{l}
              </span>
            ))}
          </motion.div>
        </div>

        {/* right — arch portrait (3D avatar with cursor parallax) */}
        <div className="relative mx-auto w-full max-w-md">
          <OrbitBadge />
          <motion.div
            style={{ y: yImg, perspective: 900 }}
            className="relative overflow-hidden rounded-b-3xl rounded-t-[240px] border border-cream/20"
            data-testid="hero-arch-image"
          >
            <motion.div
              style={{ rotateX: rotX, rotateY: rotY, x: tx, y: ty, transformStyle: "preserve-3d" }}
              className="relative"
              data-testid="hero-parallax-inner"
            >
              <img
                src={IMAGES.heroAvatar}
                alt="Stylized 3D avatar of Amit Rawat in trekking gear at a night camp"
                className="aspect-[3/4] w-full scale-[1.07] object-cover"
              />
              <motion.div style={{ x: bx, y: by }} className="absolute inset-0 opacity-[0.15]" aria-hidden="true" data-testid="hero-parallax-topo">
                <TopoLines className="h-full w-full" />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-t from-night/70 via-transparent to-transparent" />
            </motion.div>
            <p className="absolute bottom-5 left-0 right-0 text-center font-mono text-[9px] tracking-[0.35em] text-cream/80">
              BASE CAMP — 3,800 M
            </p>
          </motion.div>
          <div className="floaty glass absolute -right-3 top-1/4 rounded-full border border-cream/15 px-4 py-2 font-mono text-[9px] tracking-[0.25em] text-cream/80" data-testid="hero-float-tag">
            ALT 6,120 M <span className="text-ember">▲</span>
          </div>
          <div className="glass absolute -left-4 bottom-10 rounded-full border border-cream/15 px-4 py-2 font-mono text-[9px] tracking-[0.25em] text-cream/80">
            50+ TREKS LOGGED <span className="text-ember">✓</span>
          </div>
        </div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-cream/50 sm:flex"
      >
        SCROLL TO ASCEND <ArrowDown className="h-3.5 w-3.5 animate-bounce text-ember" />
      </motion.div>
    </section>
  );
}
